import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getActiveHostId } from "@/hosts";
import { Icon } from "@/components/kit";
import insonUchunIcon from "@/assets/insonUchun.svg";
import { SegIcon } from "./HomeSegment";
export type AppTab = "home" | "completed" | "news" | "profile";

interface BottomNavProps {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
}

/** Tab → Iconify nomlari (linear = inactive, bold = active). Figma §5.5 / §1. */
const ALL_TABS: {
  id: AppTab;
  labelKey: string;
  icon: string | React.ReactNode;
  iconActive: string | React.ReactNode;
}[] = [
    { id: "home", labelKey: "navHome", icon: <SegIcon src={insonUchunIcon} size={24} />, iconActive: <SegIcon src={insonUchunIcon} size={24} /> },
    {
      id: "completed",
      labelKey: "navCompleted",
      icon: "solar:check-read-linear",
      iconActive: "solar:check-read-bold",
    },
    { id: "news", labelKey: "navNews", icon: "hugeicons:news", iconActive: "hugeicons:news" },
    { id: "profile", labelKey: "navProfile", icon: "solar:user-linear", iconActive: "solar:user-bold" },
  ];

const HIDDEN_TABS: Partial<Record<AppTab, boolean>> = {
  profile: getActiveHostId() === "payme",
};

export function BottomNav({ activeTab, onTabChange }: BottomNavProps) {
  const { t } = useTranslation();
  // Portal mount: SSR/hydration xatosi bo'lmasligi uchun client-side mount paytida true.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const tabs = ALL_TABS.filter((tab) => !HIDDEN_TABS[tab.id]);

  const nav = (
    <nav
      // createPortal orqali nav <body> ga render bo'ladi — transformli parentlardan
      // qutuladi (Zoomrad/Telegram WebView/Payme'da fixed muammosi).
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 150,
        borderTop: "1px solid var(--c-line)",
        backgroundColor: "color-mix(in srgb, var(--c-surface) 90%, transparent)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        paddingBottom: `max(env(safe-area-inset-bottom), ${getActiveHostId() === "zoomrad" ? "0" : "1rem"})`,
        // paddingBottom: 0,
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        touchAction: "manipulation",
      }}
      aria-label={t("navAria")}
    >
      <div className="mx-auto flex max-w-lg items-center justify-around px-2 pt-2 pb-1">
        {tabs.map(({ id, labelKey, icon, iconActive }) => {
          const isActive = activeTab === id;
          const current = isActive ? iconActive : icon;
          return (
            <button
              key={id}
              type="button"
              aria-current={isActive ? "page" : undefined}
              onClick={() => onTabChange(id)}
              className="relative flex min-h-11 min-w-16 flex-col items-center justify-center gap-1 px-3 py-1"
              style={{ color: isActive ? "var(--primary)" : "var(--c-text-muted)" }}
            >
              <motion.span
                whileTap={{ scale: 0.88 }}
                animate={isActive ? { y: [0, -2, 0] } : { y: 0 }}
                transition={{ duration: 0.25 }}
                className="block"
              >
                {/* insonUchunIcon kabi rasm-iconlar JSX sifatida keladi — Iconify
                    wrapperi faqat string nomlarni qabul qiladi, shuning uchun
                    ReactNode bo'lsa to'g'ridan-to'g'ri render qilinadi. */}
                {typeof current === "string" ? <Icon name={current} size={24} /> : current}
              </motion.span>
              <span className="text-[12px] font-medium leading-none tracking-[0.2px]">
                {t(labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );

  if (!mounted || typeof document === "undefined") return null;
  return createPortal(nav, document.body);
}